import React, { useState, useEffect } from 'react';
import { User, Eye, Mouse, MessageCircle, ShoppingCart } from 'lucide-react';
import { useSharedCart } from './SharedCartProvider';

interface UserPresenceProps {
  className?: string;
  showCursors?: boolean;
  showActivityIndicators?: boolean;
}

interface UserCursor {
  userId: string;
  displayName: string;
  x: number;
  y: number;
  lastUpdate: number;
  isActive: boolean;
}

interface UserActivity {
  userId: string;
  displayName: string;
  activity: 'viewing' | 'typing' | 'adding' | 'voting' | 'idle';
  timestamp: number;
  data?: any;
}

export const UserPresence: React.FC<UserPresenceProps> = ({
  className = '',
  showCursors = true,
  showActivityIndicators = true,
}) => {
  const { sharedCart, onlineParticipants, sendMessage, isConnected } = useSharedCart();
  const [cursors, setCursors] = useState<Map<string, UserCursor>>(new Map());
  const [activities, setActivities] = useState<Map<string, UserActivity>>(new Map());
  const [isMouseTracking, setIsMouseTracking] = useState(false);

  // Track mouse movement for cursor sharing
  useEffect(() => {
    if (!showCursors || !isConnected || !sharedCart) return;

    let throttleTimeout: NodeJS.Timeout | null = null;
    const THROTTLE_MS = 100; // Limit cursor updates to 10fps

    const handleMouseMove = (e: MouseEvent) => {
      if (throttleTimeout) return;

      throttleTimeout = setTimeout(() => {
        const x = (e.clientX / window.innerWidth) * 100;
        const y = (e.clientY / window.innerHeight) * 100;

        sendMessage('cursor_move', {
          x: Math.round(x * 100) / 100, // Round to 2 decimal places
          y: Math.round(y * 100) / 100,
        });

        throttleTimeout = null;
      }, THROTTLE_MS);
    };

    const handleMouseEnter = () => {
      setIsMouseTracking(true);
      sendMessage('cursor_enter');
    };

    const handleMouseLeave = () => {
      setIsMouseTracking(false);
      sendMessage('cursor_leave');
    };

    document.addEventListener('mousemove', handleMouseMove, { passive: true });
    document.addEventListener('mouseenter', handleMouseEnter);
    document.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseenter', handleMouseEnter);
      document.removeEventListener('mouseleave', handleMouseLeave);
      if (throttleTimeout) {
        clearTimeout(throttleTimeout);
      }
    };
  }, [showCursors, isConnected, sharedCart, sendMessage]);

  // Handle incoming cursor and presence updates
  useEffect(() => {
    // This would be connected to the WebSocket message handler
    const handlePresenceUpdate = (message: any) => {
      switch (message.type) {
        case 'cursor_move':
          if (message.userId && message.data?.x !== undefined && message.data?.y !== undefined) {
            setCursors(prev => {
              const updated = new Map(prev);
              const participant = sharedCart?.participants.find(p => 
                p.userId === message.userId || p.guestId === message.userId
              );
              
              if (participant) {
                updated.set(message.userId, {
                  userId: message.userId,
                  displayName: participant.displayName,
                  x: message.data.x,
                  y: message.data.y,
                  lastUpdate: Date.now(),
                  isActive: true,
                });
              }
              
              return updated;
            });
          }
          break;

        case 'cursor_leave':
          if (message.userId) {
            setCursors(prev => {
              const updated = new Map(prev);
              if (updated.has(message.userId)) {
                updated.set(message.userId, {
                  ...updated.get(message.userId)!,
                  isActive: false,
                });
              }
              return updated;
            });
          }
          break;

        case 'typing_start':
          if (message.userId) {
            setUserActivity(message.userId, 'typing', message.data);
          }
          break;

        case 'typing_stop':
          if (message.userId) {
            removeUserActivity(message.userId);
          }
          break;

        case 'item_added':
        case 'item_removed':
        case 'quantity_changed':
          if (message.userId) {
            setUserActivity(message.userId, 'adding', message.data, 3000); // Show for 3 seconds
          }
          break;

        case 'item_voted':
          if (message.userId) {
            setUserActivity(message.userId, 'voting', message.data, 2000); // Show for 2 seconds
          }
          break;
      }
    };

    // In a real implementation, this would be connected to the WebSocket
    // For now, this is a placeholder for the message handling
    // handlePresenceUpdate would be called from the SharedCartProvider

    return () => {
      // Cleanup
    };
  }, [sharedCart]);

  // Cleanup old cursors
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      const CURSOR_TIMEOUT = 5000; // 5 seconds

      setCursors(prev => {
        const updated = new Map(prev);
        for (const [userId, cursor] of updated.entries()) {
          if (now - cursor.lastUpdate > CURSOR_TIMEOUT) {
            updated.delete(userId);
          }
        }
        return updated;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const setUserActivity = (userId: string, activity: UserActivity['activity'], data?: any, timeout?: number) => {
    const participant = sharedCart?.participants.find(p => 
      p.userId === userId || p.guestId === userId
    );
    
    if (!participant) return;

    setActivities(prev => {
      const updated = new Map(prev);
      updated.set(userId, {
        userId,
        displayName: participant.displayName,
        activity,
        timestamp: Date.now(),
        data,
      });
      return updated;
    });

    if (timeout) {
      setTimeout(() => {
        removeUserActivity(userId);
      }, timeout);
    }
  };

  const removeUserActivity = (userId: string) => {
    setActivities(prev => {
      const updated = new Map(prev);
      updated.delete(userId);
      return updated;
    });
  };

  const getActivityIcon = (activity: UserActivity['activity']) => {
    switch (activity) {
      case 'typing': return <MessageCircle className="w-3 h-3" />;
      case 'adding': return <ShoppingCart className="w-3 h-3" />;
      case 'voting': return <Eye className="w-3 h-3" />;
      case 'viewing': return <Eye className="w-3 h-3" />;
      default: return <User className="w-3 h-3" />;
    }
  };

  const getActivityColor = (activity: UserActivity['activity']) => {
    switch (activity) {
      case 'typing': return 'text-blue-500 bg-blue-100';
      case 'adding': return 'text-green-500 bg-green-100';
      case 'voting': return 'text-purple-500 bg-purple-100';
      case 'viewing': return 'text-gray-500 bg-gray-100';
      default: return 'text-gray-500 bg-gray-100';
    }
  };

  const getActivityDescription = (activity: UserActivity) => {
    switch (activity.activity) {
      case 'typing': return 'is typing...';
      case 'adding': return activity.data?.productName ? `added ${activity.data.productName}` : 'is adding an item';
      case 'voting': return activity.data?.vote ? `voted ${activity.data.vote}` : 'is voting';
      case 'viewing': return 'is viewing';
      default: return 'is active';
    }
  };

  if (!sharedCart || !isConnected) {
    return null;
  }

  return (
    <div className={`user-presence ${className}`}>
      {/* Cursors */}
      {showCursors && (
        <div className="fixed inset-0 pointer-events-none z-50">
          {Array.from(cursors.values()).map(cursor => {
            if (!cursor.isActive) return null;
            
            return (
              <div
                key={cursor.userId}
                className="absolute transition-all duration-100 ease-out"
                style={{
                  left: `${cursor.x}%`,
                  top: `${cursor.y}%`,
                  transform: 'translate(-2px, -2px)',
                }}
              >
                {/* Cursor */}
                <div className="relative">
                  <Mouse className="w-4 h-4 text-blue-500 drop-shadow-lg" />
                  
                  {/* User label */}
                  <div className="absolute top-4 left-0 bg-blue-500 text-white px-2 py-1 rounded text-xs font-medium whitespace-nowrap shadow-lg">
                    {cursor.displayName}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Activity Indicators */}
      {showActivityIndicators && activities.size > 0 && (
        <div className="fixed bottom-4 left-4 z-40 space-y-2 pointer-events-none">
          {Array.from(activities.values()).map(activity => (
            <div
              key={activity.userId}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg shadow-lg backdrop-blur-sm transition-all duration-300 ${getActivityColor(activity.activity)}`}
            >
              <div className="flex-shrink-0">
                {getActivityIcon(activity.activity)}
              </div>
              <div className="text-sm font-medium">
                <span className="font-semibold">{activity.displayName}</span>
                <span className="ml-1">{getActivityDescription(activity)}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Typing Indicators */}
      {Array.from(activities.values())
        .filter(activity => activity.activity === 'typing')
        .map(activity => (
          <div
            key={activity.userId}
            className="fixed bottom-20 right-4 z-40 bg-gray-800 text-white px-3 py-2 rounded-lg shadow-lg"
          >
            <div className="flex items-center gap-2">
              <div className="flex gap-1">
                <div className="w-1.5 h-1.5 bg-white rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-1.5 h-1.5 bg-white rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-1.5 h-1.5 bg-white rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
              <span className="text-sm">{activity.displayName} is typing...</span>
            </div>
          </div>
        ))}
    </div>
  );
};

// Hook for components to track their own activity
export const useUserActivity = () => {
  const { sendMessage } = useSharedCart();
  const [isTyping, setIsTyping] = useState(false);

  const startTyping = (context?: string) => {
    if (!isTyping) {
      setIsTyping(true);
      sendMessage('typing_start', { context });
    }
  };

  const stopTyping = () => {
    if (isTyping) {
      setIsTyping(false);
      sendMessage('typing_stop');
    }
  };

  const trackActivity = (activity: string, data?: any) => {
    sendMessage('activity_update', { activity, data });
  };

  return {
    startTyping,
    stopTyping,
    trackActivity,
    isTyping,
  };
};